import React, { useCallback, useEffect, useMemo, useState } from "react";
import NurturingFilters from "./NurturingFilters";
import { Center, LoadingOverlay, Stack, Stepper, Text } from "@mantine/core";
import NurturingPageHeader from "./NurturingPageHeader";
import NurturingContacts from "./NurturingContacts";
import NurturingFlow from "./NurturingFlow";
import NurturingProg from "./NurturingProg";
import { useFlowStore } from "../../../../../stores/WorkflowStore";
import StepLoader from "../../../../../components/View/StepLoader";
import { useLanguage } from "../../../../../stores/LanguageStore";
import {
  getTimezoneOffset,
  incrementHour,
} from "../../../../../functions/dates";
import {
  defaultNodeConfigs,
  defaultProgramData,
} from "../../../workflows/CreateCampaign/flow/flowSettings";
import { createList, findList } from "../../../../../api/ContactAPI";
import {
  createWorkflow,
  editWorkflow,
  programWorkflow,
} from "../../../../../api/WorkflowAPI";
import { createNurturing, editNurturing } from "../../../../../api/PRMAPI";
import { editCampaign, getProgrammations } from "../../../../../api/StatsAPI";
import { handleWorkflowGet } from "../../../workflows/CreateCampaign/flow/flowUtils";
import { getObjectWithChangedProperties } from "../../../../../functions/utilFunctions";
import { notifications } from "@mantine/notifications";
import NurturingReview from "./NurturingReview";
import { useProfileStore } from "../../../../../stores/UserStore";
import NurturingFlowHorizontal from "./NurturingFlowHorizontal";

const NurturingPage = ({ nurturing, onEdited, handleClose }) => {
  const lang = useLanguage((s) => s.language);
  const workflowProfile = useFlowStore((s) => s.profile);
  const layout = useFlowStore((s) => s.layout);
  const [activeStep, setActiveStep] = useState("filters");
  const [name, setName] = useState(nurturing?.name);
  const [filter, setFilter] = useState(
    nurturing?.filter || {
      mode: "and",
      values: [{ field_name: "id", type: "contains", value: "" }],
    },
  );
  const [list, setList] = useState(null);
  const [workflow, setWorkflow] = useState(null);
  const [oldProgramData, setOldProgramData] = useState();
  const [programData, setProgramData] = useState();

  const [activeLoading, setActiveLoading] = useState(0);
  const [loading, setLoading] = useState(false);
  const [finding, setFinding] = useState(false);

  useEffect(() => {
    async function main() {
      setFinding(true);
      try {
        if (nurturing?.contact_list_ids?.[0]) {
          findList(nurturing?.contact_list_ids?.[0]).then((r1) => {
            const listProfile = r1.data.contact_list_profile;
            setList(listProfile);

            if (listProfile?.usedInProgrammations?.length) {
              const mainProg = listProfile?.usedInProgrammations.sort(
                (a, b) => b.id - a.id,
              )[0];
              getProgrammations({
                filter: {
                  mode: "and",
                  values: [
                    {
                      field_name: "id",
                      type: "equals",
                      value: `${mainProg?.id}`,
                    },
                  ],
                },
              }).then((r2) => {
                const prog = r2.data.results[0];
                setOldProgramData({
                  ...prog,
                  time_start_sending: incrementHour(
                    prog.time_start_sending,
                    -getTimezoneOffset(),
                  ),
                });
                setProgramData({
                  ...prog,
                  time_start_sending: incrementHour(
                    prog.time_start_sending,
                    -getTimezoneOffset(),
                  ),
                });
                setWorkflow({ name: prog.workflow_name, id: prog.workflow_id });

                handleWorkflowGet({
                  id: prog.workflow_id,
                  isEdit: true,
                  onFinish: ({ newNodes }) => {
                    setFinding(false);
                  },
                });
              });
            } else {
              setFinding(false);
            }
          });
        } else {
          setFinding(false);
        }
      } catch {
        setFinding(false);
      }
    }
    if (parseInt(nurturing?.id)) {
      main();
    }
  }, []);

  const handleFinish = useCallback(async () => {
    let nodes = useFlowStore.getState().nodes;
    const noProg = nodes.length <= 1;

    if (noProg) {
      if (!name) {
        return document.getElementById("nurturing-name").focus();
      }
      try {
        setLoading(true);

        let finalList = { data: { ...list, contact_list_id: list?.id } };
        // Save list first
        setActiveLoading(0);
        if (!list?.id) {
          finalList = await createList({ name: list?.name });
        }

        // Save the nurturing
        setActiveLoading(3);
        const mainNurturing = parseInt(nurturing?.id)
          ? editNurturing
          : createNurturing;
        await mainNurturing({
          id: nurturing.id,
          name,
          contact_list_ids: [finalList.data.contact_list_id],
          filter,
        });

        setLoading(false);
        setActiveLoading(0);
        onEdited();
      } catch {
        setLoading(false);
        setActiveLoading(0);
      }
    } else {
      try {
        setLoading(true);
        let allNodes = useFlowStore.getState().nodes;

        let finalList = { data: { ...list, contact_list_id: list?.id } };
        let finalWorkflow = {
          data: {
            ...workflow,
            ...workflowProfile,
            workflow_id: workflowProfile?.id,
          },
        };
        // Save list first
        setActiveLoading(0);
        if (!list?.id) {
          finalList = await createList({ name: list?.name });
        }

        // Then save Workflow
        setActiveLoading(1);
        let timezoneOffset = getTimezoneOffset();

        let initialId = useFlowStore.getState().initialId;
        let needDummy = allNodes.find(
          (x) =>
            x?.data?.source === initialId && x?.data?.actionType === "event",
        );

        let nodes = allNodes.filter((x) => {
          if (needDummy) {
            return x.type === "action" || x.type === "start";
          } else {
            return x.type === "action";
          }
        });

        const steps = nodes.map((step, index) => {
          if (step?.data?.actionType !== "event") {
            let timeData = {};

            const needTimezone = [
              "time_start_sending_friday",
              "time_start_sending_monday",
              "time_start_sending_saturday",
              "time_start_sending_sunday",
              "time_start_sending_thursday",
              "time_start_sending_tuesday",
              "time_start_sending_wednesday",
              "time_stop_sending_friday",
              "time_stop_sending_monday",
              "time_stop_sending_sunday",
              "time_stop_sending_thursday",
              "time_stop_sending_tuesday",
              "time_stop_sending_wednesday",
              "time_stop_sending_saturday",
            ];

            needTimezone.forEach((element) => {
              if (step?.data?.[element])
                timeData[element] = incrementHour(
                  step?.data?.[element],
                  timezoneOffset,
                );
            });

            return {
              step_id: {
                type: step?.data?.exist ? "existing" : "new",
                id: step.id === "0" ? 1 : parseInt(step.id),
              },
              step_type: "action",
              position_x: step.x,
              position_y: step.y,
              parent_ids:
                step?.data?.source === initialId || step.type === "start"
                  ? []
                  : Array.isArray(step?.data?.source)
                    ? step?.data?.source.map((s) => ({
                        type: nodes.find((x) => x.id === s)?.data?.exist
                          ? "existing"
                          : "new",
                        id: parseInt(s),
                      }))
                    : [
                        {
                          type: nodes.find((x) => x.id === step.data.source)
                            ?.data?.exist
                            ? "existing"
                            : "new",
                          id:
                            step.data.source === "0"
                              ? 1
                              : parseInt(step.data.source),
                        },
                      ],
              action_type: step.data.actionType,
              model_id: step.data?.model_id,
              integration_id: step.data?.integration_id,
              pool_id: step.data?.pool_id,
              is_initial: needDummy
                ? step.type === "start"
                : step.data?.source === initialId,
              signature_id: step.data?.signature_id,
              limit_send_per_day:
                step.data?.limit_send_per_day ||
                defaultNodeConfigs?.limit_send_per_day,
              sending_weekends_allowed:
                step.data?.sending_weekends_allowed ||
                defaultNodeConfigs?.sending_weekends_allowed,
              time_start_sending: incrementHour(
                step.data?.time_start_sending ||
                  defaultNodeConfigs?.time_start_sending,
                timezoneOffset,
              ),
              time_stop_sending: incrementHour(
                step.data?.time_stop_sending ||
                  defaultNodeConfigs?.time_stop_sending,
                timezoneOffset,
              ),
              blacklist_ids:
                step.data?.blacklist_ids || defaultNodeConfigs?.blacklist_ids,
              email_linkedin_exclude:
                step.data?.email_linkedin_exclude ||
                defaultNodeConfigs?.email_linkedin_exclude,
              show_reply_as_conversation:
                step.data?.show_reply_as_conversation !== null
                  ? step.data?.show_reply_as_conversation
                  : defaultNodeConfigs?.show_reply_as_conversation,
              allow_massmailing:
                step.data?.show_reply_as_conversation ||
                defaultNodeConfigs?.allow_massmailing,
              use_smtp_fallback_server:
                step.data?.show_reply_as_conversation ||
                defaultNodeConfigs?.use_smtp_fallback_server,
              disable_auto_remove_responders:
                step.data?.disable_auto_remove_responders,
              ...timeData,
            };
          } else {
            return {
              step_id: {
                type: step?.data?.exist ? "existing" : "new",
                id: parseInt(step.id),
              },
              step_type: "event",
              event_type: step.data?.event_type,
              position_x: step.x,
              position_y: step.y,
              filter: step.data?.filter,
              which_links_clicked: step.data?.which_links_clicked,
              when_minutes:
                step.data?.when_minutes || defaultNodeConfigs?.when_minutes,
              name: step.data?.name,
              parent_ids: [
                {
                  type: nodes.find((x) => x.id === step.data.source)?.data
                    ?.exist
                    ? "existing"
                    : "new",
                  id: step.data.source === "0" ? 1 : parseInt(step.data.source),
                },
              ],
            };
          }
        });
        console.log("here");

        if (!workflowProfile?.id) {
          finalWorkflow = await createWorkflow({
            name: `${name} - workflow`,
            auto_remove_responders: workflowProfile?.auto_remove_responders,
            steps,
            nurturing: true,
          });
        } else {
          editWorkflow({
            id: workflowProfile?.id,
            name: `${name} - workflow`,
            auto_remove_responders: workflowProfile?.auto_remove_responders,
            steps,
            nurturing: true,
          });
        }

        // Program the workflow
        setActiveLoading(2);
        const finalProgramData = {
          ...defaultProgramData,
          contactlist_ids: [finalList.data.contact_list_id],
          ...programData,
          date_start: programData?.date_start
            ? new Date(programData.date_start).toISOString()
            : new Date().toISOString(),
          date_stop: programData?.date_stop
            ? new Date(programData.date_stop).toISOString()
            : null,
        };

        finalProgramData.time_start_sending = incrementHour(
          finalProgramData.time_start_sending,
          getTimezoneOffset(),
        );
        finalProgramData.time_stop_sending = incrementHour(
          finalProgramData.time_stop_sending,
          getTimezoneOffset(),
        );

        if (
          programData?.id &&
          programData?.workflow_id === workflowProfile?.id &&
          new Date(oldProgramData.date_start) < new Date()
        ) {
          delete finalProgramData.contactlist_ids;
          delete finalProgramData.blacklist_ids;
          delete finalProgramData.exclude_programmation_ids;
        }

        const diff =
          programData?.id && workflowProfile?.id === oldProgramData?.id
            ? getObjectWithChangedProperties(
                {
                  ...oldProgramData,
                  date_start: new Date(
                    oldProgramData?.date_start,
                  ).toISOString(),
                  time_stop_sending: incrementHour(
                    oldProgramData.time_stop_sending,
                    getTimezoneOffset(),
                  ),
                  time_start_sending: incrementHour(
                    oldProgramData.time_start_sending,
                    getTimezoneOffset(),
                  ),
                },
                finalProgramData,
              )
            : null;

        const main =
          programData?.id && programData?.workflow_id === workflowProfile?.id
            ? editCampaign
            : programWorkflow;

        if (
          (diff && Object.keys(diff).length > 0) ||
          programData?.workflow_id !== workflowProfile?.id
        ) {
          await main({
            workflow_id:
              programData?.id &&
              oldProgramData?.workflow_id === workflowProfile?.id
                ? workflowProfile?.id
                : finalWorkflow.data.workflow_id,
            programmation_id: programData?.id,
            data:
              programData?.id &&
              oldProgramData?.workflow_id === workflowProfile?.id
                ? diff
                : finalProgramData,
          });
        }

        // Save the nurturing
        setActiveLoading(3);
        const mainNurturing = parseInt(nurturing?.id)
          ? editNurturing
          : createNurturing;
        await mainNurturing({
          id: nurturing.id,
          name,
          contact_list_ids: [finalList.data.contact_list_id],
          filter,
        });

        setLoading(false);
        setActiveLoading(0);
        onEdited();
      } catch (error) {
        console.error("Error in handleFinish:", error);
        setLoading(false);
        setActiveLoading(0);
      }
    }
  }, [
    filter,
    list,
    name,
    nurturing?.id,
    oldProgramData,
    onEdited,
    programData,
    workflow,
    workflowProfile,
  ]);

  const component = useMemo(() => {
    if (activeStep === "filters") {
      return (
        <NurturingFilters
          filter={filter}
          setFilter={setFilter}
          setActiveStep={setActiveStep}
          handleClose={handleClose}
        />
      );
    }
    if (activeStep === "contacts") {
      return (
        <NurturingContacts
          list={list}
          setList={setList}
          setActiveStep={setActiveStep}
          isPast={
            oldProgramData
              ? new Date(oldProgramData.date_start) < new Date()
              : false
          }
        />
      );
    }
    if (activeStep === "workflow") {
      if (layout === "horizontal") {
        return (
          <NurturingFlowHorizontal
            nurturing={nurturing}
            setActiveStep={setActiveStep}
          />
        );
      } else {
        return (
          <NurturingFlow nurturing={nurturing} setActiveStep={setActiveStep} />
        );
      }
    }
    if (activeStep === "program") {
      return (
        <NurturingProg
          nurturing={nurturing}
          filter={filter}
          oldProgramData={oldProgramData}
          list={list}
          programData={programData}
          setProgramData={setProgramData}
          setActiveStep={setActiveStep}
        />
      );
    }
    if (activeStep === "review") {
      return (
        <NurturingReview
          nurturing={nurturing}
          filter={filter}
          list={list}
          programData={programData}
          setActiveStep={setActiveStep}
        />
      );
    }
  }, [
    activeStep,
    filter,
    handleClose,
    list,
    oldProgramData,
    nurturing,
    programData,
    layout,
  ]);

  const loadingSteps = useMemo(() => {
    return [
      {
        label: lang.prm.nurturing.loading_steps.step1_title,
        description: lang.prm.nurturing.loading_steps.step1_text,
      },
      {
        label: lang.prm.nurturing.loading_steps.step2_title,
        description: lang.prm.nurturing.loading_steps.step2_text,
      },
      {
        label: lang.prm.nurturing.loading_steps.step3_title,
        description: lang.prm.nurturing.loading_steps.step3_text,
      },
      {
        label: lang.prm.nurturing.loading_steps.step4_title,
        description: lang.prm.nurturing.loading_steps.step4_text,
      },
    ];
  }, []);

  useEffect(() => {
    return () => {
      useFlowStore.getState().setNodes([]);
      useFlowStore.getState().setEdges([]);
      useFlowStore.getState().setProfile({
        id: null,
        name: "",
        tags: [],
        folder: null,
        sharing: [],
        auto_remove_responders: true,
      });
    };
  }, []);

  return (
    <Stack flex={1} pos={"relative"}>
      {loading && (
        <LoadingOverlay
          visible={true}
          overlayProps={{ radius: "sm", blur: 2 }}
          loaderProps={{
            children: (
              <Center
                p={"xl"}
                bg={"#ffffff99"}
                style={{ borderRadius: 16, flexDirection: "column" }}
                pb={"sm"}
              >
                <Text size="lg" mb={16}>
                  {lang.linkedin_accounts.create.dialog.loading_text}
                </Text>
                <Stepper
                  active={activeLoading}
                  orientation="vertical"
                  size="lg"
                >
                  {loadingSteps.map((step, index) => (
                    <Stepper.Step
                      key={index}
                      label={step.label}
                      description={step.description}
                      loading={activeLoading === index}
                    />
                  ))}
                </Stepper>
              </Center>
            ),
          }}
        />
      )}

      <LoadingOverlay visible={finding} overlayProps={{ blur: 1 }} />

      <NurturingPageHeader
        activeStep={activeStep}
        setActiveStep={setActiveStep}
        nurturing={nurturing}
        name={name}
        setName={setName}
        handleClose={handleClose}
        handleFinish={handleFinish}
      />

      {component}
    </Stack>
  );
};

export default NurturingPage;
